/**
 * A trucking load's billing and rates, as the API's rate card fills them
 * (api#950, API-4a) and as an office user types them.
 *
 * WHAT THE API DOES, so the screen does not do it again:
 * - On a new load, every kind the request did not type (haul, wait time, fuel
 *   surcharge) is filled from the rate card and its source recorded as 'card'.
 *   A typed value is 'manual', an explicit 0 included. A kind the card lacks is
 *   left EMPTY and named in `rate_card_refusals`; booking is not blocked.
 * - On an edit before the BOL is signed, changing customer, truck, load type,
 *   billing type or date re-fills the card values. Manual values stay.
 * - Once the BOL is signed, any rate or quantity edit is 409 LOAD_RATES_LOCKED.
 *
 * So the screen sends only what the user TYPED. A blank field is absent, never
 * 0: on a new load a blank is "use the card", and an explicit "0" is a real
 * $0 the user chose. Nothing here prices, totals or taxes anything.
 *
 * Reads are snake_case (`l.*` on GET /trucking/loads/bols/:id); writes are
 * camelCase (POST and PATCH /trucking/loads, which read `body.flatHaulRate`).
 */

export const BILLING_TYPES = ['per_load', 'per_hour', 'day_rate', 'per_km', 'per_m3'] as const;
export type BillingType = (typeof BILLING_TYPES)[number];

/**
 * Which columns carry the haul rate and the quantity for each billing type —
 * the API's own HAUL_RATE_COLUMN (lib/trucking-rates.ts), read and write
 * spellings side by side. Exhaustive over `billing_type`.
 */
export const BILLING: Record<BillingType, {
  label: string;
  rateLabel: string;
  rateRead: string;
  rateWrite: string;
  qtyLabel: string | null;
  qtyRead: string | null;
  qtyWrite: string | null;
}> = {
  per_load: { label: 'Per load', rateLabel: 'Haul rate (per load)', rateRead: 'flat_haul_rate', rateWrite: 'flatHaulRate', qtyLabel: null, qtyRead: null, qtyWrite: null },
  per_hour: { label: 'Per hour', rateLabel: 'Hourly rate', rateRead: 'hourly_rate', rateWrite: 'hourlyRate', qtyLabel: 'Billable hours', qtyRead: 'billable_hours', qtyWrite: 'billableHours' },
  day_rate: { label: 'Day rate', rateLabel: 'Day rate', rateRead: 'day_rate', rateWrite: 'dayRate', qtyLabel: 'Billable days', qtyRead: 'billable_days', qtyWrite: 'billableDays' },
  per_km: { label: 'Per km', rateLabel: 'Rate per km', rateRead: 'rate_per_km', rateWrite: 'ratePerKm', qtyLabel: 'Total km', qtyRead: 'km_driven', qtyWrite: 'kmDriven' },
  per_m3: { label: 'Per m³', rateLabel: 'Rate per m³', rateRead: 'rate_per_m3', rateWrite: 'ratePerM3', qtyLabel: 'Volume (m³)', qtyRead: 'volume_m3', qtyWrite: 'volumeM3' },
};

export const isBillingType = (v: unknown): v is BillingType =>
  typeof v === 'string' && (BILLING_TYPES as readonly string[]).includes(v);

/** The kinds the card fills, as `rate_card_refusals[].kind` names them. */
export const RATE_KIND_LABEL: Record<string, string> = {
  haul: 'Haul',
  wait_time: 'Wait time',
  fuel_surcharge: 'Fuel surcharge',
  carrier_passthrough: 'Carrier passthrough',
};
export const rateKindLabel = (k: unknown) =>
  typeof k === 'string' && Object.prototype.hasOwnProperty.call(RATE_KIND_LABEL, k) ? RATE_KIND_LABEL[k] : '—';

/**
 * Where a value came from. 'card' and 'manual' are the API's whole vocabulary;
 * no source means the card had nothing and nobody typed one.
 */
export function sourceLabel(source: unknown): string {
  if (source === 'card') return 'Rate card';
  if (source === 'manual') return 'Entered';
  return '—';
}

export interface RateCardRefusal {
  kind: string;
  code?: string;
  error: string;
}

/** `rate_card_refusals` off a create or edit response; anything malformed is dropped, never guessed. */
export function readRefusals(body: unknown): RateCardRefusal[] {
  const list = (body as { rate_card_refusals?: unknown } | null)?.rate_card_refusals;
  if (!Array.isArray(list)) return [];
  return list
    .filter((r): r is RateCardRefusal => !!r && typeof r === 'object' && typeof (r as RateCardRefusal).error === 'string')
    .map((r) => ({ kind: String(r.kind ?? ''), code: r.code, error: r.error }));
}

/** The form's own state: every value is the text in its box. */
export interface RatesDraft {
  billingType: BillingType;
  haulRate: string;
  quantity: string;
  waitHourlyRate: string;
  freeWaitMinutes: string;
  fuelBasis: '' | 'fixed' | 'percent';
  fuelValue: string;
  provinceState: string;
}

export const EMPTY_RATES: RatesDraft = {
  billingType: 'per_load',
  haulRate: '',
  quantity: '',
  waitHourlyRate: '',
  freeWaitMinutes: '',
  fuelBasis: '',
  fuelValue: '',
  provinceState: '',
};

/**
 * A typed number, or undefined for a blank box. A typed "0" is 0 — a real $0.
 * Text that is not a number is a problem for the caller to refuse, so it
 * comes back as NaN rather than being quietly dropped.
 */
export function typedNumber(text: string): number | undefined {
  const t = text.trim().replace(/,/g, '');
  if (t === '') return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** The first box holding something that is not a number, by its label; null when all are fine. */
export function invalidField(d: RatesDraft): string | null {
  const b = BILLING[d.billingType];
  const checks: [string, string][] = [
    [b.rateLabel, d.haulRate],
    ...(b.qtyLabel ? [[b.qtyLabel, d.quantity] as [string, string]] : []),
    ['Wait rate (per hour)', d.waitHourlyRate],
    ['Free wait minutes', d.freeWaitMinutes],
    [d.fuelBasis === 'percent' ? 'Fuel surcharge (%)' : 'Fuel surcharge', d.fuelValue],
  ];
  for (const [label, text] of checks) {
    const n = typedNumber(text);
    if (n !== undefined && Number.isNaN(n)) return label;
  }
  if (d.fuelValue.trim() !== '' && d.fuelBasis === '') return 'Fuel surcharge type';
  return null;
}

/**
 * The create body's billing and rate keys. Only typed values are sent; a blank
 * is left out so the card fills it. Fuel goes as `fuelBasis` with exactly one
 * of `fuelAmount` / `fuelPercent`, the shape the API's fuelShapeError requires.
 */
export function ratesCreateFields(d: RatesDraft): Record<string, unknown> {
  const b = BILLING[d.billingType];
  const out: Record<string, unknown> = { billingType: d.billingType };
  const put = (key: string | null, text: string) => {
    if (!key) return;
    const n = typedNumber(text);
    if (n !== undefined && !Number.isNaN(n)) out[key] = n;
  };
  put(b.rateWrite, d.haulRate);
  put(b.qtyWrite, d.quantity);
  put('waitHourlyRate', d.waitHourlyRate);
  put('freeWaitMinutes', d.freeWaitMinutes);
  const fuel = typedNumber(d.fuelValue);
  if (d.fuelBasis && fuel !== undefined && !Number.isNaN(fuel)) {
    out.fuelBasis = d.fuelBasis;
    out[d.fuelBasis === 'fixed' ? 'fuelAmount' : 'fuelPercent'] = fuel;
  }
  if (d.provinceState) out.provinceState = d.provinceState;
  return out;
}

const text = (v: unknown) => (v === null || v === undefined ? '' : String(v));

/** The draft a load's current values make (snake_case read). */
export function draftFromLoad(load: Record<string, unknown>): RatesDraft {
  const billingType = isBillingType(load.billing_type) ? load.billing_type : 'per_load';
  const b = BILLING[billingType];
  const basis = load.fuel_basis === 'fixed' || load.fuel_basis === 'percent' ? load.fuel_basis : '';
  return {
    billingType,
    haulRate: text(load[b.rateRead]),
    quantity: b.qtyRead ? text(load[b.qtyRead]) : '',
    waitHourlyRate: text(load.wait_hourly_rate),
    freeWaitMinutes: text(load.free_wait_minutes),
    fuelBasis: basis,
    fuelValue: basis === 'fixed' ? text(load.fuel_amount) : basis === 'percent' ? text(load.fuel_percent) : '',
    provinceState: text(load.province_state),
  };
}

/**
 * The PATCH body for an edit: only what CHANGED from the load as it stands.
 * Sending a key at all marks that kind 'manual' on the API, so an untouched box
 * must not be sent — that would turn a card value into a manual one. A box the
 * user emptied sends null, which clears the value.
 */
export function ratesEditFields(before: RatesDraft, after: RatesDraft): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const num = (t: string) => {
    const n = typedNumber(t);
    return n === undefined ? null : n;
  };
  const b = BILLING[after.billingType];
  if (after.billingType !== before.billingType) out.billingType = after.billingType;
  // A changed billing type moves the rate to another column, so the new
  // column is written whenever it holds something or was edited.
  if (after.haulRate !== before.haulRate || (after.billingType !== before.billingType && after.haulRate.trim() !== '')) {
    out[b.rateWrite] = num(after.haulRate);
  }
  if (b.qtyWrite && (after.quantity !== before.quantity || (after.billingType !== before.billingType && after.quantity.trim() !== ''))) {
    out[b.qtyWrite] = num(after.quantity);
  }
  if (after.waitHourlyRate !== before.waitHourlyRate) out.waitHourlyRate = num(after.waitHourlyRate);
  if (after.freeWaitMinutes !== before.freeWaitMinutes) out.freeWaitMinutes = num(after.freeWaitMinutes);
  if (after.fuelBasis !== before.fuelBasis || after.fuelValue !== before.fuelValue) {
    const v = num(after.fuelValue);
    if (!after.fuelBasis || v === null) {
      out.fuelBasis = null; out.fuelAmount = null; out.fuelPercent = null;
    } else {
      out.fuelBasis = after.fuelBasis;
      out.fuelAmount = after.fuelBasis === 'fixed' ? v : null;
      out.fuelPercent = after.fuelBasis === 'percent' ? v : null;
    }
  }
  if (after.provinceState !== before.provinceState) out.provinceState = after.provinceState || null;
  return out;
}
